import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Box, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Text, Badge, HStack, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { DataGrid } from '../../components/data-grid/data-grid';
import ConfigChangeList from './ConfigChangeList';
import { useConfigurationAudit } from './hooks/useConfigurationAudit';
import { useConfigurationEffectiveValue } from './hooks/useConfigurationEffectiveValue';
import { useConfigurationSave } from './hooks/useConfigurationSave';
import { buildConfigurationColumnDefs, buildConfigurationRowData } from './config-helpers';
const ConfigurationDetailDrawer = ({ isOpen, onClose, configDetail, serviceLines, tenantId })=>{
    const [editingServiceLine, setEditingServiceLine] = useState(null);
    const isSystemLevel = tenantId === '*';
    // Use the extracted hooks
    const { configurationAuditData, handlePageChange } = useConfigurationAudit({
        isOpen,
        tenantId,
        configKeyname: configDetail?.keyname
    });
    const { getEffectiveValueForServiceLine } = useConfigurationEffectiveValue(configDetail, tenantId);
    const { handleSaveRow } = useConfigurationSave(configDetail, tenantId, ()=>setEditingServiceLine(null));
    // Show service line rows for both system level and tenant level
    const rowData = useMemo(()=>buildConfigurationRowData(configDetail, serviceLines, tenantId, getEffectiveValueForServiceLine, isSystemLevel), [
        configDetail,
        serviceLines,
        tenantId,
        getEffectiveValueForServiceLine,
        isSystemLevel
    ]);
    // Clean columnDefs using the extracted utility function
    const columnDefs = useMemo(()=>buildConfigurationColumnDefs(editingServiceLine, setEditingServiceLine, handleSaveRow, isSystemLevel), [
        editingServiceLine,
        handleSaveRow,
        isSystemLevel
    ]);
    const gridOptions = useMemo(()=>({
            rowHeight: 120,
            suppressCellFocus: true,
            pagination: false,
            paginationPageSize: undefined
        }), []);
    return /*#__PURE__*/ _jsxs(Drawer, {
        isOpen: isOpen,
        placement: "right",
        size: "xl",
        onClose: onClose,
        blockScrollOnMount: false,
        children: [
            /*#__PURE__*/ _jsx(DrawerOverlay, {}),
            /*#__PURE__*/ _jsxs(DrawerContent, {
                children: [
                    /*#__PURE__*/ _jsx(DrawerCloseButton, {}),
                    /*#__PURE__*/ _jsx(DrawerHeader, {
                        pr: "5rem",
                        children: /*#__PURE__*/ _jsxs(HStack, {
                            spacing: 3,
                            children: [
                                /*#__PURE__*/ _jsx(Text, {
                                    children: configDetail?.displayName || configDetail?.keyname || 'Configuration'
                                }),
                                isSystemLevel && /*#__PURE__*/ _jsx(Badge, {
                                    colorScheme: "purple",
                                    fontSize: "0.8em",
                                    children: "System Level"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(DrawerBody, {
                        children: configDetail ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                isSystemLevel && /*#__PURE__*/ _jsxs(Alert, {
                                    status: "warning",
                                    mb: 4,
                                    borderRadius: "md",
                                    children: [
                                        /*#__PURE__*/ _jsx(AlertIcon, {}),
                                        /*#__PURE__*/ _jsxs(Box, {
                                            children: [
                                                /*#__PURE__*/ _jsx(AlertTitle, {
                                                    fontSize: "sm",
                                                    children: "System-Level Configuration"
                                                }),
                                                /*#__PURE__*/ _jsx(AlertDescription, {
                                                    fontSize: "xs",
                                                    children: "You are viewing system-level configurations that affect all tenants. Changes should be made carefully as they will impact the entire system."
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Box, {
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: "1 1 0",
                                    minH: "400px",
                                    width: "100%",
                                    height: "auto",
                                    maxH: "100%",
                                    overflowY: "auto",
                                    mb: 6,
                                    children: /*#__PURE__*/ _jsx(DataGrid, {
                                        rowData: rowData,
                                        columnDefs: columnDefs,
                                        gridOptions: gridOptions
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Divider, {
                                    mb: 10
                                }),
                                /*#__PURE__*/ _jsxs(Box, {
                                    mb: 5,
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            fontSize: "sm",
                                            fontWeight: "bold",
                                            mb: 1,
                                            children: "DESCRIPTION"
                                        }),
                                        /*#__PURE__*/ _jsx(Text, {
                                            fontSize: "xs",
                                            color: "gray.500",
                                            mb: 2,
                                            children: configDetail.keyname
                                        }),
                                        /*#__PURE__*/ _jsx(Text, {
                                            children: configDetail.description || 'No description provided.'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Divider, {
                                    mb: 10
                                }),
                                /*#__PURE__*/ _jsxs(Box, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            fontSize: "sm",
                                            fontWeight: "bold",
                                            mb: 2,
                                            children: "CHANGE HISTORY"
                                        }),
                                        /*#__PURE__*/ _jsx(Box, {
                                            display: "flex",
                                            flexDirection: "column",
                                            sx: {
                                                height: 'min(55vh, 560px)',
                                                minHeight: '240px'
                                            },
                                            children: /*#__PURE__*/ _jsx(ConfigChangeList, {
                                                changes: configurationAuditData.content,
                                                auditData: configurationAuditData,
                                                onPageChange: handlePageChange
                                            })
                                        })
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(Text, {
                            children: "Loading configuration details..."
                        })
                    })
                ]
            })
        ]
    });
};
export default ConfigurationDetailDrawer;
